class VUTSectionReviewsSlider extends HTMLElement {
  connectedCallback() {
    if (this.dataset.vutReviewsInitialized === 'true') return;

    this.track = this.querySelector('[data-vut-reviews-track]');
    this.slides = Array.from(
      this.querySelectorAll('[data-vut-reviews-slide]')
    );
    this.dotsWrapper = this.querySelector('[data-vut-reviews-dots]');
    this.dots = Array.from(
      this.querySelectorAll('[data-vut-reviews-dot]')
    );
    this.prevButton = this.querySelector('[data-vut-reviews-prev]');
    this.nextButton = this.querySelector('[data-vut-reviews-next]');

    this.activeIndex = null;
    this.updateFrame = null;
    this.heightFrame = null;
    this.scrollEndTimer = null;

    if (
      !this.track ||
      !this.prevButton ||
      !this.nextButton ||
      !this.slides.length
    ) {
      return;
    }

    this.dataset.vutReviewsInitialized = 'true';

    this.handlePrevClick = () => {
      this.scrollSlider(-1);
    };

    this.handleNextClick = () => {
      this.scrollSlider(1);
    };

    this.handleScroll = () => {
      this.scheduleUpdate();
      this.scheduleActiveImageLoad();
    };

    this.handleResize = () => {
      this.scheduleUpdate();
    };

    this.handleDotClick = event => {
      const dot = event.currentTarget;
      const index = Number(dot.dataset.vutReviewsDotIndex);

      if (Number.isNaN(index)) return;

      /*
       * Load the selected slide, three before it
       * and three after it.
       */
      this.loadImagesAroundIndex(index, 3);
      this.scrollToSlide(index);
    };

    this.prevButton.addEventListener(
      'click',
      this.handlePrevClick
    );

    this.nextButton.addEventListener(
      'click',
      this.handleNextClick
    );

    this.track.addEventListener(
      'scroll',
      this.handleScroll,
      { passive: true }
    );

    window.addEventListener(
      'resize',
      this.handleResize
    );

    this.dots.forEach(dot => {
      dot.addEventListener(
        'click',
        this.handleDotClick
      );
    });

    /*
     * Update the track height whenever a card changes size.
     */
    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(() => {
        this.scheduleHeightUpdate();
      });

      this.slides.forEach(slide => {
        const card = slide.querySelector(
          '.vut-section-reviews__card'
        );

        if (card) {
          this.resizeObserver.observe(card);
        }
      });
    }

    requestAnimationFrame(() => {
      this.loadInitialImages(5);
      this.updateSlider();
    });
  }

  disconnectedCallback() {
    this.prevButton?.removeEventListener(
      'click',
      this.handlePrevClick
    );

    this.nextButton?.removeEventListener(
      'click',
      this.handleNextClick
    );

    this.track?.removeEventListener(
      'scroll',
      this.handleScroll
    );

    window.removeEventListener(
      'resize',
      this.handleResize
    );

    this.dots?.forEach(dot => {
      dot.removeEventListener(
        'click',
        this.handleDotClick
      );
    });

    this.resizeObserver?.disconnect();

    if (this.updateFrame) {
      cancelAnimationFrame(this.updateFrame);
      this.updateFrame = null;
    }

    if (this.heightFrame) {
      cancelAnimationFrame(this.heightFrame);
      this.heightFrame = null;
    }

    if (this.scrollEndTimer) {
      clearTimeout(this.scrollEndTimer);
      this.scrollEndTimer = null;
    }

    this.dataset.vutReviewsInitialized = 'false';
  }

  scheduleUpdate() {
    if (this.updateFrame) return;

    this.updateFrame = requestAnimationFrame(() => {
      this.updateFrame = null;
      this.updateSlider();
    });
  }

  scheduleHeightUpdate() {
    if (this.heightFrame) return;

    this.heightFrame = requestAnimationFrame(() => {
      this.heightFrame = null;
      this.updateSliderHeight();
    });
  }

  scheduleActiveImageLoad() {
    if (this.scrollEndTimer) {
      clearTimeout(this.scrollEndTimer);
    }

    /*
     * Load the active image after touch or trackpad scrolling stops.
     */
    this.scrollEndTimer = setTimeout(() => {
      this.scrollEndTimer = null;

      const activeIndex = this.getActiveIndex();
      this.loadImageAtIndex(activeIndex);
    }, 120);
  }

  getMaxScrollLeft() {
    return Math.max(
      0,
      this.track.scrollWidth - this.track.clientWidth
    );
  }

  isAtStart() {
    return this.track.scrollLeft <= 1;
  }

  isAtEnd() {
    return (
      this.track.scrollLeft >=
      this.getMaxScrollLeft() - 2
    );
  }

  getSlideStep() {
    const firstSlide = this.slides[0];

    if (!firstSlide) {
      return this.track.clientWidth;
    }

    const trackStyles = window.getComputedStyle(
      this.track
    );

    const gap =
      parseFloat(
        trackStyles.columnGap ||
        trackStyles.gap
      ) || 0;

    return (
      firstSlide.getBoundingClientRect().width +
      gap
    );
  }

  getSlideScrollLeft(slide) {
    const trackRect =
      this.track.getBoundingClientRect();

    const slideRect =
      slide.getBoundingClientRect();

    const snapAlign =
      window.getComputedStyle(slide).scrollSnapAlign || '';

    if (snapAlign.includes('center')) {
      const trackCenter =
        trackRect.left +
        trackRect.width / 2;

      const slideCenter =
        slideRect.left +
        slideRect.width / 2;

      return (
        this.track.scrollLeft +
        (slideCenter - trackCenter)
      );
    }

    return (
      this.track.scrollLeft +
      (slideRect.left - trackRect.left)
    );
  }

  loadImageInSlide(slide) {
    if (!slide) return false;

    const image = slide.querySelector(
      '[data-vut-reviews-lazy-image]'
    );

    /*
     * Skip images that have already been requested.
     */
    if (
      !image ||
      image.dataset.vutReviewsRequested === 'true'
    ) {
      return false;
    }

    const src = image.dataset.src;
    const srcset = image.dataset.srcset;
    const sizes = image.dataset.sizes;

    if (!src) return false;

    image.dataset.vutReviewsRequested = 'true';
    image.loading = 'eager';
    image.classList.add('is-loading');

    const finishLoading = () => {
      if (
        image.dataset.vutReviewsLoaded === 'true'
      ) {
        return;
      }

      image.dataset.vutReviewsLoaded = 'true';

      image.classList.remove('is-loading');
      image.classList.add('is-loaded');

      this.scheduleHeightUpdate();
    };

    image.addEventListener(
      'load',
      finishLoading,
      { once: true }
    );

    image.addEventListener(
      'error',
      () => {
        image.classList.remove('is-loading');
        image.classList.add('is-error');

        this.scheduleHeightUpdate();
      },
      { once: true }
    );

    if (sizes) {
      image.sizes = sizes.trim();
    }

    if (srcset) {
      image.srcset = srcset.trim();
    }

    image.src = src;

    /*
     * Handle images already available in the browser cache.
     */
    if (
      image.complete &&
      image.naturalWidth > 0
    ) {
      finishLoading();
    }

    return true;
  }

  loadImageAtIndex(index) {
    if (
      index < 0 ||
      index >= this.slides.length
    ) {
      return false;
    }

    return this.loadImageInSlide(
      this.slides[index]
    );
  }

  loadInitialImages(count = 5) {
    const amountToLoad = Math.min(
      count,
      this.slides.length
    );

    for (
      let index = 0;
      index < amountToLoad;
      index += 1
    ) {
      this.loadImageAtIndex(index);
    }
  }

  loadImagesInDirection(
    startIndex,
    direction,
    count = 3
  ) {
    /*
     * Next:
     * startIndex, startIndex + 1, startIndex + 2
     *
     * Previous:
     * startIndex, startIndex - 1, startIndex - 2
     */
    for (
      let offset = 0;
      offset < count;
      offset += 1
    ) {
      const index =
        startIndex + offset * direction;

      this.loadImageAtIndex(index);
    }
  }

  loadImagesAroundIndex(
    centerIndex,
    distance = 3
  ) {
    /*
     * Load three slides before, the selected slide,
     * and three slides after.
     */
    for (
      let offset = -distance;
      offset <= distance;
      offset += 1
    ) {
      this.loadImageAtIndex(
        centerIndex + offset
      );
    }
  }

  scrollToSlide(index) {
    const slide = this.slides[index];

    if (!slide) return;

    this.track.scrollTo({
      left: this.getSlideScrollLeft(slide),
      behavior: 'smooth'
    });
  }

  scrollSlider(direction) {
    const maxScrollLeft =
      this.getMaxScrollLeft();

    if (maxScrollLeft <= 1) return;

    const activeIndex =
      this.getActiveIndex();

    const willWrapToStart =
      direction > 0 &&
      this.isAtEnd();

    const targetIndex = willWrapToStart
      ? 0
      : direction > 0
        ? Math.min(
            activeIndex + 1,
            this.slides.length - 1
          )
        : Math.max(
            activeIndex - 1,
            0
          );

    /*
     * Load the destination slide and two more slides
     * in the direction of navigation.
     */
    this.loadImagesInDirection(
      targetIndex,
      direction,
      3
    );

    if (willWrapToStart) {
      this.track.scrollTo({
        left: 0,
        behavior: 'smooth'
      });

      return;
    }

    this.track.scrollBy({
      left: this.getSlideStep() * direction,
      behavior: 'smooth'
    });
  }

  getActiveIndex() {
    const trackRect =
      this.track.getBoundingClientRect();

    const trackCenter =
      trackRect.left +
      trackRect.width / 2;

    let activeIndex = 0;
    let closestDistance = Infinity;

    this.slides.forEach((slide, index) => {
      const slideRect =
        slide.getBoundingClientRect();

      const slideCenter =
        slideRect.left +
        slideRect.width / 2;

      const distance = Math.abs(
        slideCenter - trackCenter
      );

      if (distance < closestDistance) {
        closestDistance = distance;
        activeIndex = index;
      }
    });

    return activeIndex;
  }

  getVisibleSlides() {
    const trackRect =
      this.track.getBoundingClientRect();

    return this.slides.filter(slide => {
      const slideRect =
        slide.getBoundingClientRect();

      const horizontalOverlap =
        Math.min(
          slideRect.right,
          trackRect.right
        ) -
        Math.max(
          slideRect.left,
          trackRect.left
        );

      return horizontalOverlap > 1;
    });
  }

  updateSliderHeight() {
    const visibleSlides =
      this.getVisibleSlides();

    const slidesToMeasure =
      visibleSlides.length
        ? visibleSlides
        : [
            this.slides[
              this.getActiveIndex()
            ]
          ].filter(Boolean);

    const tallestVisibleSlide =
      slidesToMeasure.reduce(
        (maxHeight, slide) => {
          const card = slide.querySelector(
            '.vut-section-reviews__card'
          );

          if (!card) return maxHeight;

          const cardHeight =
            card.getBoundingClientRect().height;

          return Math.max(
            maxHeight,
            cardHeight
          );
        },
        0
      );

    if (tallestVisibleSlide > 0) {
      this.track.style.height =
        `${Math.ceil(tallestVisibleSlide)}px`;
    }
  }

  updateDots(activeIndex) {
    this.dots.forEach((dot, index) => {
      if (index === activeIndex) {
        dot.setAttribute(
          'aria-current',
          'true'
        );
      } else {
        dot.removeAttribute(
          'aria-current'
        );
      }
    });

    this.scrollDotsToIndex(activeIndex);
  }

  updateDotsOverflow() {
    if (!this.dotsWrapper) return;

    const isOverflowing =
      this.dotsWrapper.scrollWidth >
      this.dotsWrapper.clientWidth + 1;

    this.dotsWrapper.classList.toggle(
      'is-overflowing',
      isOverflowing
    );
  }

  scrollDotsToIndex(index) {
    if (!this.dotsWrapper) return;

    const dot = this.dots[index];

    if (!dot) return;

    this.updateDotsOverflow();

    if (
      !this.dotsWrapper.classList.contains(
        'is-overflowing'
      )
    ) {
      return;
    }

    const wrapperRect =
      this.dotsWrapper.getBoundingClientRect();

    const dotRect =
      dot.getBoundingClientRect();

    const targetScrollLeft =
      this.dotsWrapper.scrollLeft +
      (dotRect.left - wrapperRect.left) -
      wrapperRect.width / 2 +
      dotRect.width / 2;

    this.dotsWrapper.scrollTo({
      left: targetScrollLeft,
      behavior: 'smooth'
    });
  }

  updateButtons() {
    const isOverflowing =
      this.getMaxScrollLeft() > 1;

    this.classList.toggle(
      'is-overflowing',
      isOverflowing
    );

    this.prevButton.disabled =
      !isOverflowing ||
      this.isAtStart();

    this.nextButton.disabled =
      !isOverflowing;
  }

  updateSlider() {
    const activeIndex =
      this.getActiveIndex();

    this.updateButtons();
    this.updateDotsOverflow();
    this.updateSliderHeight();

    if (activeIndex !== this.activeIndex) {
      this.activeIndex = activeIndex;
      this.updateDots(activeIndex);
    }
  }
}

if (
  !customElements.get(
    'vut-section-reviews-slider'
  )
) {
  customElements.define(
    'vut-section-reviews-slider',
    VUTSectionReviewsSlider
  );
}